import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { FileText, Link2, Share2 } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import NewDossierDialog from "@/components/client/NewDossierDialog";
import ShareDialog from "@/components/client/ShareDialog";
import ClientsTable from "@/components/dashboard/ClientsTable";
import { filterClients, sortClients } from "@/components/dashboard/pipeline";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SCOPE_KEY = "autobio_clients_scope";

const readScope = () => {
  try {
    return localStorage.getItem(SCOPE_KEY) === "all" ? "all" : "mine";
  } catch (_) {
    return "mine";
  }
};

const writeScope = (value) => {
  try {
    localStorage.setItem(SCOPE_KEY, value);
  } catch (_) {
    // Le stockage peut être indisponible en navigation privée.
  }
};

export default function DashboardScreen({ user }) {
  const canSeeAll = Boolean(user?.features?.clients_scope_all);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scope, setScope] = useState(() => (canSeeAll ? readScope() : "mine"));
  const [waitingOn, setWaitingOn] = useState("all");
  // Repli quand le presse-papiers refuse (Safari perd l'autorisation apres
  // l'appel reseau) : on montre le lien pour qu'il reste copiable a la main.
  const [lienARecopier, setLienARecopier] = useState(null);
  const navigate = useNavigate();

  const loadClients = useCallback(async (wanted) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/clients`, { params: { scope: wanted } });
      setClients(res.data.clients || []);
    } catch (err) {
      if (err?.response?.status === 403 && wanted === "all") {
        writeScope("mine");
        setScope("mine");
        return;
      }
      console.error("Failed to load clients:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/users`);
      setUsers(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Failed to load users:", err);
    }
  }, []);

  useEffect(() => {
    loadClients(scope);
  }, [loadClients, scope]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  // Le lien du formulaire n'etait affiche qu'une fois, a la creation du
  // dossier : ferme la fenetre sans copier et il fallait recreer un dossier.
  // Le jeton est en base, l'API sait le redonner — on le relit a la demande.
  const handleCopyFormLink = async (event, sessionId) => {
    event.stopPropagation();
    let url;
    try {
      const res = await axios.get(`${API}/formulaire/dossiers/${sessionId}`);
      url = `${window.location.origin}${res.data.form_path}`;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossible de récupérer le lien");
      return;
    }
    // Le lien est en main : un echec ici ne concerne plus que la copie.
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Lien du formulaire copié");
    } catch (_) {
      setLienARecopier(url);
    }
  };

  if (!user) return null;

  const otherUsers = users.filter((candidate) => candidate.email !== user.email);
  const usersById = Object.fromEntries(users.map((candidate) => [candidate.id, candidate]));
  const now = Date.now();
  const rows = sortClients(filterClients(clients, { waitingOn }), now);

  const handleScopeChange = (value) => {
    if (!value || value === scope) return;
    writeScope(value);
    setScope(value);
  };

  const handleWaitingOnChange = (value) => {
    if (!value || value === waitingOn) return;
    setWaitingOn(value);
  };

  const renderActions = (client) => (
    <div className="flex items-center justify-end">
      {client.can_open !== false && client.steps?.formulaire?.state === "lien_envoye" && (
        <Button
          variant="ghost"
          size="icon"
          title="Copier le lien du formulaire"
          onClick={(event) => handleCopyFormLink(event, client.session_id)}
        >
          <Link2 className="h-4 w-4" />
        </Button>
      )}
      {client.can_open !== false && !client.is_shared && otherUsers.length > 0 && (
        <ShareDialog sessionId={client.session_id} users={otherUsers}>
          <Button
            variant="ghost"
            size="icon"
            title="Partager le dossier"
            onClick={(event) => event.stopPropagation()}
          >
            <Share2 className="h-4 w-4" />
          </Button>
        </ShareDialog>
      )}
    </div>
  );

  return (
    <div className="bg-background">
      <div className="mx-auto max-w-7xl px-4 py-8">
        <div className="mb-8 flex items-center justify-between gap-4">
          <h1 className="text-2xl font-bold">Vos dossiers clients</h1>
          <NewDossierDialog onCreated={() => loadClients(scope)} />
        </div>

        <div className="mb-6 flex flex-wrap gap-4">
          {canSeeAll && (
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">Dossiers :</span>
              <ToggleGroup
                type="single"
                value={scope}
                onValueChange={handleScopeChange}
                aria-label="Dossiers"
              >
                <ToggleGroupItem value="mine">Les miens</ToggleGroupItem>
                <ToggleGroupItem value="all">Tous</ToggleGroupItem>
              </ToggleGroup>
            </div>
          )}

          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">En attente de :</span>
            <ToggleGroup
              type="single"
              value={waitingOn}
              onValueChange={handleWaitingOnChange}
              aria-label="En attente de"
            >
              <ToggleGroupItem value="all">Tous</ToggleGroupItem>
              <ToggleGroupItem value="client">Le client</ToggleGroupItem>
              <ToggleGroupItem value="equipe">L'équipe</ToggleGroupItem>
            </ToggleGroup>
          </div>
        </div>

        {loading ? (
          <p className="py-12 text-center text-muted-foreground">Chargement...</p>
        ) : clients.length === 0 && waitingOn === "all" ? (
          <div className="py-16 text-center">
            <FileText className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
            <p className="text-muted-foreground">Aucun dossier client pour l'instant</p>
          </div>
        ) : rows.length === 0 ? (
          <p className="py-12 text-center text-muted-foreground">
            Aucun dossier ne correspond.
          </p>
        ) : (
          <ClientsTable
            clients={rows}
            usersById={usersById}
            now={now}
            onOpen={(client) => navigate(`/clients/${client.session_id}`)}
            renderActions={renderActions}
          />
        )}
      </div>

      <Dialog open={!!lienARecopier} onOpenChange={(open) => !open && setLienARecopier(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Lien du formulaire</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            La copie automatique a été refusée par le navigateur. Sélectionne le
            lien et copie-le à la main.
          </p>
          <Input readOnly value={lienARecopier || ""} onFocus={(event) => event.target.select()} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
